<script setup lang="ts">
import type { ErrorObject } from '@vuelidate/core';
import type { DatePickerInstance } from '@vuepic/vue-datepicker';
import dayjs from 'dayjs';

const multiCalendarsOptions = {
	solo: false,
	static: false,
	count: 2,
};

const props = defineProps({
	id: {
		type: String,
		default: 'vue-time-picker',
	},
	date: {
		type: [String, Array] as PropType<string | string[]>,
		default: '',
	},
	time: {
		type: String,
		default: '',
	},
	isDateOnly: {
		type: Boolean,
		default: false,
	},
	minDate: {
		type: [Date, String] as PropType<Date | string | null>,
		default: '',
	},
	timezone: {
		type: String,
		required: true,
	},
	datePlaceholder: {
		type: String,
		default: '',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	dateErrors: {
		type: Array as PropType<Array<ErrorObject>>,
		default: () => [],
	},
	timeErrors: {
		type: Array as PropType<Array<ErrorObject>>,
		default: () => [],
	},
	timeDropDirection: {
		type: String,
		default: 'auto',
	},
	isTeleportDatePicker: {
		type: Boolean,
		default: false,
	},
	isMultiCalendarRange: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits<{
	(e: 'onDateChange', value: string | string[]): void
	(e: 'onTimeChange', value: string): void
}>();
const datePickerEl = ref<DatePickerInstance | null>(null);
const timePickerEl = ref<HTMLElement | null>(null);

const timezoneValue = computed(() => props.timezone || DEFAULT_TIMEZONE);
const computedDatePlaceholder = computed(() => props.datePlaceholder || 'Select date');

const modelValue = computed({
	get: () => {
		if (props.isMultiCalendarRange) {
			if (!props.date || !Array.isArray(props.date)) {
				return [];
			}
			return props.date.map((d) => (d ? dayjs(d, DATE_TIME_ISO8601_FORMAT).toDate() : null));
		}
		if (typeof props.date === 'string' && props.date) {
			return dayjs(props.date, DATE_TIME_ISO8601_FORMAT).toDate();
		}
		return null;
	},
	set: (value: Date | Date[] | null) => {
		if (props.isMultiCalendarRange) {
			if (Array.isArray(value)) {
				// Emit the formatted dates for all selected dates
				const emittedValue = value.map((v) => (v ? dayjs(v).format(DATE_TIME_ISO8601_FORMAT) : ''));
				emit('onDateChange', emittedValue);
			}
		} else if (value && !Array.isArray(value)) {
			const formattedDate = dayjs(value).startOf('day').format(DATE_TIME_ISO8601_FORMAT);
			emit('onDateChange', formattedDate);
		}
	},
});

const displayText = computed(() => {
	if (props.isMultiCalendarRange) {
		if (!props.date || !Array.isArray(props.date)) {
			return '';
		}

		// Handle different array lengths
		if (props.date.length === 0) {
			return '';
		} else if (props.date.length === 1) {
			return convertDateTimeWithStrictTimezone(
				props.date[0],
				timezoneValue.value,
				DATE_PICKER_FORMAT,
			);
		}

		const [start, end] = props.date;
		const formattedStart = convertDateTimeWithStrictTimezone(start, timezoneValue.value, DATE_PICKER_FORMAT);
		const formattedEnd = convertDateTimeWithStrictTimezone(end, timezoneValue.value, DATE_PICKER_FORMAT);
		return `${formattedStart} - ${formattedEnd}`;
	}

	if (typeof props.date === 'string' && props.date) {
		return convertDateTimeWithStrictTimezone(props.date, timezoneValue.value, DATE_PICKER_FORMAT);
	}

	return '';
});

const timeValue = computed({
	get: () => props.time,
	set: (value) => {
		const timeObj = dayjs(value, 'hh:mm A', true);
		if (!timeObj.isValid()) {
			return;
		}
		emit('onTimeChange', value);
	},
});

const minDateValue = computed(() => {
	if (props.minDate) {
		return props.minDate;
	} else if (props.minDate === null) {
		return '';
	}
	return dayjs()
		.tz(timezoneValue.value)
		.startOf('day')
		.format(`${AMERICAN_DATE_FORMAT} ${TIME_24_HOUR_FORMAT}`);
});

function handleDatePickerKeyDown(event: KeyboardEvent) {
	if (event.code === 'Tab' && datePickerEl) {
		event.preventDefault();
		datePickerEl.value?.openMenu();
	}
}

function handleTimePickerClick() {
	timePickerEl.value?.focus();
}

onMounted(() => {
	timePickerEl.value = document.getElementById(props.id);
});
</script>

<template>
  <div :class="['date-time-group', { '--date-only': isDateOnly }]">
    <VueDatePicker
      ref="datePickerEl"
      v-model="modelValue"
      class="cursor-pointer"
      :min-date="minDateValue"
      :timezone="timezoneValue"
      :enable-time-picker="false"
      :month-change-on-scroll="false"
      :clearable="false"
      :disabled="disabled"
      :range="isMultiCalendarRange"
      :multi-calendars="isMultiCalendarRange ? multiCalendarsOptions : false"
      :teleport-center="isTeleportDatePicker"
      position="left"
      six-weeks="fair"
      week-start="0"
      auto-apply
      hide-input-icon
      arrow-navigation
      utc
      @keydown="handleDatePickerKeyDown"
    >
      <template #trigger>
        <BaseTextInput
          v-model="displayText"
          class="date-picker-input"
          :placeholder="computedDatePlaceholder"
          :error-message="getValidationErrorMessage(dateErrors)"
          :disabled="disabled"
          is-show-custom-error
          readonly
        >
          <template #rightIcon>
            <CalendarIcon
              color="#667085"
              :class="['custom-icon', { disabled: disabled }]"
            />
          </template>
        </BaseTextInput>
      </template>
    </VueDatePicker>
    <template v-if="!isDateOnly">
      <BaseTextInput
        v-model="timeValue"
        class="time-picker-input cursor-pointer"
        placeholder="Time"
        :error-message="getValidationErrorMessage(timeErrors)"
        :disabled="disabled"
        is-show-custom-error
        readonly
        @click="handleTimePickerClick"
      >
        <template #rightIcon>
          <ClockIcon :class="['custom-icon', { disabled: disabled }]" />
        </template>
      </BaseTextInput>
      <VueTimepicker
        :id="id"
        v-model="timeValue"
        :format="TIME_12_HOUR_2_DIGITS_FORMAT"
        :minute-interval="5"
        :disabled="disabled"
        :drop-direction="timeDropDirection"
        placeholder="Time"
        container-id="auto-dropdown-containter"
        close-on-complete
        advanced-keyboard
        hide-clear-button
        auto-scroll
      />
    </template>
    <div id="auto-dropdown-containter" />
  </div>
</template>

<style lang="scss" scoped>
.date-time-group {
  position: relative;

  &:not(.--date-only) {
    display: grid;
    column-gap: spacings-get(2);
    grid-template-columns: 1fr rem(140);
  }

  .date-picker-input {
    margin-bottom: 0;

    :deep(.text-field-wrapper.--disabled) {
      border-radius: spacings-get(2) !important;
    }
  }

  .custom-icon {
    position: absolute;
    top: rem(23 - 10);
    right: rem(14);
    width: rem(20);
    height: rem(20);
    cursor: pointer;

    &.disabled {
      cursor: initial;
    }
  }
}
</style>
